import { configurationApi, statusApi } from '@/api/';
import { HeaderTitleBar, HelpFactoryComponent, SortableList, StatusDashboardIcon } from '@/components/common';
import { AccessSummary, EventSummary, EventTableWidget, OnlineActiveUsers, PeopleCredentials, PerformanceStatus, QueuedDownload } from '@/components/widget/';
import { ProfileSetting } from '@/model/ConfigurationModel';
import { EnrollmentBlankImages } from '@/model/EnrollmentModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { WidgetInfo } from '@/model/StatusModel';
import { StoreEvent } from '@/store';
import { setEnrollmentBlankImages } from '@/store/enrollment/actions';
import { Spin } from 'antd';
import arrayMove from 'array-move';
import React from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import './statusDashboard.scss';

// Internal state for the component
interface State {
	isLoading: boolean;
	widgetList: WidgetInfo[];
	visible: boolean;
}

interface DispatchProps {
	handleSetEnrollmentBlankImages: (images: EnrollmentBlankImages) => void;
}

interface OwnProps {}

interface StateProps {}

// Normal properties for the component
type Props = DispatchProps & OwnProps & StateProps;
const topicId: string = 'StatusDashboard';

class StatusDashboardComponent extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);

		this.state = {
			isLoading: true,
			widgetList: [],
			visible: false,
		};
	}

	componentDidMount() {
		const { handleSetEnrollmentBlankImages } = this.props;
		statusApi
			.retrieveStatusDashboard()
			.then(response => {
				this.setState({
					isLoading: false,
					widgetList: response.WidgetList,
					visible: false,
				});
				handleSetEnrollmentBlankImages({
					BlankProfilePictureBase64: response.BlankProfilePictureBase64,
					BlankProfileSignatureBase64: response.BlankProfileSignatureBase64,
				});
			})
			.catch(e => Logger.writeErrorLog(e));
	}

	componentDidCatch(error, errorInfo) {
		Logger.writeErrorLog(`${error}: ${errorInfo}`);
	}

	compareWidgetList = (orderList: WidgetInfo[], newOrderList: WidgetInfo[]) => {
		if (orderList.length !== newOrderList.length) {
			return true;
		}

		let hasChange = false;
		for (let i = 0; i < orderList.length; i++) {
			if (orderList[i].id !== newOrderList[i].id) {
				hasChange = true;
				break;
			}
		}

		return hasChange;
	};

	saveWidgetOrder(newWidgetOrder: WidgetInfo[]) {
		const setting: ProfileSetting = {
			Name: 'StatusDashboardGridLayout',
			ObjectId: 17, //'Web'
			Value: JSON.stringify(newWidgetOrder),
		};

		configurationApi.saveSettingChanges(setting).catch(e => Logger.writeErrorLog(e));
	}

	private handleSortEnd = ({ oldIndex, newIndex }) => {
		const { widgetList } = this.state;

		const newWidgetsOrder = arrayMove(widgetList, oldIndex, newIndex);
		if (this.compareWidgetList(widgetList, newWidgetsOrder)) {
			this.setState({
				widgetList: newWidgetsOrder,
			});
			this.saveWidgetOrder(newWidgetsOrder);
		}
	};

	render() {
		const { isLoading, widgetList } = this.state;

		const renderItem = (item: WidgetInfo) => {
			let renderComponent = <></>;
			const key = item.id;
			let className;
			switch (key) {
				case 'Widget-AccessSummary':
					className = 'status-widget-full-width';
					renderComponent = <AccessSummary />;
					break;

				case 'Widget-People':
					className = 'status-widget-half-width';
					renderComponent = <PeopleCredentials widgetInfo={item} />;
					break;

				case 'Widget-Downloads':
					className = 'status-widget-full-width';
					renderComponent = <QueuedDownload />;
					break;

				case 'Widget-Events':
					className = 'status-widget-full-width';
					renderComponent = <EventTableWidget />;
					break;

				case 'Widget-EventsSummary':
					className = 'status-widget-half-width';
					renderComponent = <EventSummary />;
					break;

				case 'Widget-PerformanceStatus':
					className = 'status-widget-half-width';
					renderComponent = <PerformanceStatus widgetInfo={item} />;
					break;

				case 'Widget-OnlineActiveUsers':
					className = 'status-widget-half-width';
					renderComponent = <OnlineActiveUsers widgetInfo={item} />;
					break;
			}

			return (
				<SortableList.Item id={item.id} className={className}>
					{renderComponent}
				</SortableList.Item>
			);
		};

		return (
			<>
				<HeaderTitleBar
					title={_('StatusDashboard')}
					icon={<StatusDashboardIcon />}
					helpButton={<HelpFactoryComponent helpFeature={HelpFeature.StatusDashboard} topicId={topicId} size='medium' isLight />}
				/>
				<div id='statusDashboardContainer' className='status-dashboard-container'>
					<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className={'spin-container status-dashboard-spin-container'}>
						<SortableList<WidgetInfo>
							containerClassName={'container-class-name'}
							items={widgetList.filter(w => w !== undefined)}
							onChange={this.handleSortEnd}
							renderItem={renderItem}
						/>
					</Spin>
				</div>
			</>
		);
	}
}

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, StoreEvent>, ownProps: OwnProps): DispatchProps => ({
	handleSetEnrollmentBlankImages: (images: EnrollmentBlankImages) => {
		dispatch(setEnrollmentBlankImages(images));
	},
});

const StatusDashboardPage = connect<StateProps, DispatchProps, OwnProps>(null, mapDispatchToProps)(StatusDashboardComponent);

export { StatusDashboardPage };
